// Traducción inglesa de las guías. Overrides por slug; no incluir
// ogImage, relatedServices, relatedProblems ni updatedAt (se heredan).
export const guidesEn = [
  {
    slug: "por-que-catastro-y-escritura-no-coinciden",
    title: "Why don't Catastro and the title deed match?",
    category: "Catastro",
    seoTitle: "Why Catastro and the deed show different surface areas | MideFinca",
    metaDescription:
      "Deeds by apparent extent, historical mapping and poorly recorded segregations: the real causes why Catastro and the deed don't match, and what to check first.",
    excerpt: "The real causes of the most common discrepancy between Catastro and title, and what to check before acting.",
    h1: "Why don't Catastro and the title deed match?",
    sections: [
      {
        h2: "Two systems that grew apart",
        paragraphs: [
      "The Land Registry documents rights: who owns what and over which property. Catastro is a fiscal and territorial inventory: it draws parcels and assigns them values. Although they converge today, for decades they worked separately, and plots accumulate different data in each one.",
      "The result is that the same plot can have one surface area in the deed, another in Catastro and a third in reality. Neither of the first two is automatically the right one.",
        ],
      },
      {
        h2: "The most common causes of the discrepancy",
        paragraphs: ["In our fieldwork in the Canary Islands, these are the causes that appear again and again:"],
        list: [
      "Deeds by apparent extent: the surface area was declared approximately, sometimes over a century ago, without measurement.",
      "Historical cadastral mapping: the first graphic representation of many areas was made with limited precision, and its errors persist.",
      "Undocumented land movements: strip purchases, transfers between neighbours or word-of-mouth boundary changes.",
      "Segregations and groupings recorded in only one of the two sources.",
      "Digitisation errors when the old deed was incorporated into modern systems.",
        ],
      },
      {
        h2: "What to check before taking any step",
        paragraphs: [
      "Before starting any procedure you need to know which figure is wrong, and that requires measuring. The reasonable sequence is: georeferenced measurement of the plot, comparison with the cadastral representation and review of the title history.",
      "With that diagnosis you know whether the discrepancy lies in Catastro, in the deed or in both, and which channel may apply. Starting procedures without that prior check is working blind: sometimes the 'correction' makes the problem worse.",
        ],
      },
      {
        h2: "What can be corrected and who corrects it?",
        paragraphs: [
      "Amending cadastral data belongs to the Catastro office, through its procedures. Correcting the title or registering a different surface area goes through the notary and the Land Registry. In all these files, the starting technical evidence is the same: a georeferenced measurement and, where applicable, a GML file.",
      "What no serious technician can promise in advance is the outcome: each procedure is resolved according to its regulations and its evidence. What can be guaranteed is that the technical documentation is well measured, well calculated and well validated.",
        ],
      },
    ],
  },
  {
    slug: "que-es-un-archivo-gml",
    title: "What is a GML file and when do you need one?",
    category: "GML & georeferencing",
    seoTitle: "What is a plot GML file and when do you need it | MideFinca",
    metaDescription:
      "The GML is the georeferenced geometry of your plot that notaries and the Land Registry have required since Act 13/2015. What it contains, when you'll be asked for it and why it must start from a real measurement.",
    excerpt: "The file notaries and the Land Registry have required since 2015: what it contains, when they demand it and how to make it properly.",
    h1: "What is a GML file of a plot?",
    sections: [
      {
        h2: "Your plot's geometry, in official coordinates",
        paragraphs: [
      "GML stands for Geography Markup Language. In practice, it is a file containing your plot's geometry, its vertices and outline, expressed in official coordinates of the ETRS89 system. It is the standardised way of saying 'this is my parcel, exactly here, with exactly this shape'.",
      "Since Act 13/2015, the georeferenced graphic representation of the plot forms part of many notarial and registry files, and the GML is its exchange format with Catastro.",
        ],
      },
      {
        h2: "When you'll be asked for it",
        paragraphs: ["The most common situations in which the notary or the Land Registry requires the GML:"],
        list: [
      "Sales, inheritances and donations in which the graphic representation is incorporated.",
      "Segregations, divisions and groupings of parcels.",
      "Declarations of new construction and extensions.",
      "Rectifications of boundaries or surface area in the title.",
        ],
      },
      {
        h2: "Why a GML is not drawn: it is measured",
        paragraphs: [
      "There are tools that let you generate a GML by drawing the outline over an aerial photograph. That file can be formally valid and factually wrong: if the drawn geometry doesn't match the measured reality, an incorrect datum is being incorporated into the title that will later have to be corrected.",
      "The correct process is the reverse: measure the plot on the ground with GNSS equipment, compute the vertex coordinates, build the geometry according to Catastro's specifications and validate the file with the official tools before delivering it.",
        ],
      },
      {
        h2: "What a well-made GML must include",
        paragraphs: [
      "Vertices with measured coordinates, tolerances conforming to the specifications of the Directorate-General of Catastro, correct topology (no spurious overlaps or gaps) and validation passed in the official tools. If it represents constructions, each one with its geometry and its position within the parcel.",
        ],
      },
    ],
  },
  {
    slug: "como-saber-metros-reales-finca",
    title: "How to know the real square metres of a plot",
    category: "Surveying",
    seoTitle: "How to know the real square metres of a plot | MideFinca",
    metaDescription:
      "Neither Catastro nor the deed guarantees your plot's real surface area. We explain how a parcel is measured with precision and how reliable each data source is.",
    excerpt: "Listing, deed and Catastro can state three different figures. This is how you get the true one.",
    h1: "How to know the real square metres of a plot",
    sections: [
      {
        h2: "Three data sources, three levels of reliability",
        paragraphs: [
      "When you ask how many square metres a plot has, you can get an answer from three places: the listing or the owner's word, the deed, and Catastro. None of the three guarantees reality: the listing usually copies the other two, the deed may be an apparent extent declared a century ago, and Catastro is a cartographic representation with its own historical margin of error.",
      "The fourth source is the only one that measures reality: the technical survey on the ground.",
        ],
      },
      {
        h2: "How a plot is measured with precision",
        paragraphs: [
      "Professional measurement is done with precision GNSS receivers, which obtain coordinates by satellite, and, when coverage or vegetation requires it, a total station. The coordinates of all the parcel's vertices and of the relevant boundary features are captured.",
      "With those vertices the complete geometry is computed: surface area, perimeter and shape. The result is not an approximation: it is a traceable measurement, expressed in the official coordinate system.",
        ],
      },
      {
        h2: "What to do with the result",
        paragraphs: ["Once the plot is measured, the real figure allows you to:"],
        list: [
      "Know which documentary source departs from reality and by how many metres.",
      "Negotiate a purchase with objective data.",
      "Decide whether it is worth starting a correction procedure in Catastro or in the title.",
      "Generate the georeferenced graphic representation (GML) if a procedure requires it.",
        ],
      },
      {
        h2: "Can I measure it myself?",
        paragraphs: [
      "Mobile apps and online viewers give useful estimates to get an idea, but their precision depends on the phone's GPS and the base orthophoto: errors of several metres per vertex are common. To sell, buy or prove something before a third party, the measurement must be technical and traceable.",
        ],
      },
    ],
  },
  {
    slug: "quien-puede-medir-una-finca",
    title: "Who can measure a plot?",
    category: "Surveying",
    seoTitle: "Who can measure a plot and with what guarantees? | MideFinca",
    metaDescription:
      "Which professionals can measure a plot with technical validity, what distinguishes a professional measurement from an informal one and what you should demand when commissioning it.",
    excerpt: "Not all measurements are equal: which professional can measure your plot and what guarantees they should offer.",
    h1: "Who can measure a plot?",
    sections: [
      {
        h2: "Measuring well requires equipment, method and technical responsibility",
        paragraphs: [
      "Anyone can estimate a plot's surface area with a mobile app or an online viewer. But when the measurement will be used for something (a sale, a Catastro file, a GML for the notary or a boundary dispute) a technical measurement is needed: precision equipment, coordinates in the official system and a professional who answers for the work.",
      "The difference is not only precision (centimetres versus metres of error) but traceability: a technical measurement documents how it was measured, with what equipment and with what result, and can be defended before third parties.",
        ],
      },
      {
        h2: "Which professionals do this work",
        paragraphs: [
      "In Spain, plot measurement and the associated technical documentation are usually carried out by professionals with competence in surveying, mapping and construction:",
        ],
        list: [
      "Technical engineers and engineers in geomatics and surveying.",
      "Architects and building engineers (aparejadores), especially when the measurement includes buildings.",
      "Other graduates with competence in topographic work according to the type of document and its purpose.",
        ],
      },
      {
        h2: "What you should demand when commissioning a measurement",
        paragraphs: ["Regardless of the degree, a serious measurement must include:"],
        list: [
      "Fieldwork with precision equipment (GNSS, total station), not a drawing over an aerial photo.",
      "Coordinates in the official reference system (ETRS89, zone 28 in the Canary Islands).",
      "A plan with the measured geometry and a record of how it was obtained.",
      "Identification of the technician responsible for the work.",
        ],
      },
      {
        h2: "What if the document is for Catastro, a notary or the Land Registry?",
        paragraphs: [
      "When the document is submitted to a file, the signature of a competent technician is a usual requirement, and the content must meet the specifications of the corresponding procedure. It is worth commissioning it from someone who knows those requirements in advance: redoing a badly conceived job costs more than doing it right from the start.",
        ],
      },
    ],
  },
  {
    slug: "cuando-necesito-georreferenciar-una-finca",
    title: "When do I need to georeference a plot?",
    category: "GML & georeferencing",
    seoTitle: "When you need to georeference a plot | MideFinca",
    metaDescription:
      "Sales, inheritances, segregations, new construction and Catastro corrections: the cases in which you'll need your plot's georeferencing and why.",
    excerpt: "The procedures in which you'll be asked for the georeferenced plot and what it exactly means.",
    h1: "When do I need to georeference a plot?",
    sections: [
      {
        h2: "What georeferencing means",
        paragraphs: [
      "Georeferencing a plot means fixing the position of its vertices in official coordinates of the ETRS89 system (in the Canary Islands, UTM zone 28). From that moment, the plot stops being described with words ('to the north, lands of…') and gains an exact, verifiable position on the official mapping.",
      "Georeferencing is the technical basis of the graphic representation that Catastro and the Land Registry have used since Act 13/2015.",
        ],
      },
      {
        h2: "The cases in which you'll need it",
        paragraphs: ["These are the most common situations in notarial and registry practice:"],
        list: [
      "Sales, inheritances and donations in which the plot's graphic representation is incorporated into the deed.",
      "Segregations, divisions and groupings of parcels: the new geometry must be defined in coordinates.",
      "Declarations and extensions of new construction, which usually require the representation of the parcel and the building.",
      "Corrections of discrepancies with Catastro: the technical evidence is a georeferenced measurement.",
      "Boundary conflicts or doubts, where vertex coordinates provide objectivity.",
        ],
      },
      {
        h2: "Can I georeference it myself with online tools?",
        paragraphs: [
      "There are tools that let you draw the geometry over an orthophoto. The problem is that a geometry drawn by eye is not a measured geometry: if it doesn't match the reality on the ground, the error gets incorporated into the official documentation and correcting it later is more expensive.",
      "Serious georeferencing starts from measurement on the ground with GNSS equipment and coordinate computation with tolerances conforming to Catastro's specifications.",
        ],
      },
    ],
  },
  {
    slug: "como-saber-donde-estan-los-limites-de-una-parcela",
    title: "How to know where a parcel's boundaries are",
    category: "Boundaries",
    seoTitle: "How to know where your parcel's boundaries are | MideFinca",
    metaDescription:
      "Markers, deed, Catastro and measurement: what sources exist to know your parcel's real boundaries, how reliable each one is and how they are checked.",
    excerpt: "Deed, Catastro, markers and fences: how reliable each source is and how boundaries are really checked.",
    h1: "How to know where a parcel's boundaries are",
    sections: [
      {
        h2: "The available sources and their real reliability",
        paragraphs: [
      "To know where a parcel begins and ends there are several sources, and it is worth knowing their reliability before trusting them:",
        ],
        list: [
      "Markers and boundary stones: old physical marks. Useful if preserved, but many have disappeared or been moved.",
      "Fences and walls: they reflect current occupation, not necessarily the documentary limit.",
      "The deed: describes the extent and the adjoining boundaries, but old descriptions can be ambiguous today.",
      "Catastro: draws the parcel on the mapping, but its representation is a graphic reference with its own historical margin of error, not legal proof of boundaries.",
        ],
      },
      {
        h2: "The technical check: measure and compare",
        paragraphs: [
      "The objective way to know the boundaries is to measure the parcel on the ground, locate the existing physical boundary features and compare the resulting geometry with the deed and with Catastro.",
      "When the vertices can be determined, they are set out on the ground and marked: you will know exactly where each corner of your property lies.",
        ],
      },
      {
        h2: "If the measured limit doesn't match what's on the ground",
        paragraphs: [
      "It is frequent for the current fence not to match the documentary limit. In that case the technical documentation records the measured situation and the discrepancy; the way to resolve it (agreement with the neighbour or legal action) depends on the case and may require other professionals.",
        ],
      },
    ],
  },
  {
    slug: "vecino-no-esta-de-acuerdo-con-el-lindero",
    title: "What to do if a neighbour disagrees with the boundary",
    category: "Boundaries",
    seoTitle: "Neighbour disagrees with the boundary: what to do | MideFinca",
    metaDescription:
      "If your neighbour disputes the plot's boundary, the first step is an objective measurement. We explain the reasonable steps, from agreement with data to legal action.",
    excerpt: "Before arguing, measure: the reasonable steps when a neighbour disputes the plot's boundary.",
    h1: "What to do if a neighbour disagrees with the boundary",
    sections: [
      {
        h2: "First, data: the objective measurement",
        paragraphs: [
      "Most boundary conflicts are sustained on assumptions: each party believes the limit is where they remember or where it suits them. The reasonable first step is to replace opinions with a technical measurement of the plot, compared with the deed and Catastro.",
      "With a measured plan on the table, many disputes cool down: both parties see the same data.",
        ],
      },
      {
        h2: "Agreement with data is the cheapest path",
        paragraphs: [
      "If the measurement clarifies the position of the limit and both parties accept it, the agreement can be documented and, if you want more firmness, formalised with notarial intervention. This scenario is usually faster and much cheaper than any contentious alternative.",
      "Neither the technician nor anyone else can force the neighbour to accept a boundary willingly: the measurement provides the objective evidence, but acceptance is voluntary until there is a court ruling.",
        ],
      },
      {
        h2: "If there is no agreement: legal action",
        paragraphs: [
      "When the disagreement persists, there is the judicial demarcation procedure. In it, the court appoints the expert who will determine the limit: neither party chooses the judicial expert.",
      "The prior technical documentation (measurement, plans and documentary comparison) remains very useful as party evidence for your lawyer, even though the final determination belongs to the court expert and the judge.",
        ],
      },
    ],
  },
  {
    slug: "que-comprobar-antes-de-comprar-un-terreno",
    title: "What to check before buying land",
    category: "Buying & selling",
    seoTitle: "What to check before buying land in the Canary Islands | MideFinca",
    metaDescription:
      "Real surface area, boundaries, consistency between Catastro and deed, declared constructions and planning: the checklist before buying land.",
    excerpt: "The technical and documentary checklist that prevents surprises after signing.",
    h1: "What to check before buying land",
    sections: [
      {
        h2: "What you see is not always what you buy",
        paragraphs: [
      "When buying land you sign on documents, not on what seems to be seen during the visit. The listing may copy Catastro's surface area, the deed may record an extent from a century ago and the fence may not be where it should. Each of those figures can tell a different story.",
        ],
      },
      {
        h2: "The essential technical checks",
        paragraphs: ["Before signing, it is worth verifying:"],
        list: [
      "Real measured surface area: how many square metres it really has, not how many the papers say.",
      "Boundaries: where the limits are and whether the current enclosure matches the documentation.",
      "Documentary consistency: whether reality, Catastro and deed tell the same story or there are discrepancies.",
      "Existing constructions: whether there are buildings and whether they are declared in the deed and Catastro.",
      "Visible accesses and easements: passageways, pipelines or easements affecting the land.",
        ],
      },
      {
        h2: "The legal and planning checks",
        paragraphs: [
      "The registry situation, charges and the land's planning classification are reviewed with the registry extract, the Land Registry and the municipal planning; the notary, your lawyer or the administration are involved there. The technical measurement and physical verification part is what we provide from MideFinca.",
      "If the check detects discrepancies, you will have data to negotiate the price, demand that the seller regularise before signing or walk away from the deal in time.",
        ],
      },
    ],
  },
  {
    slug: "como-acreditar-antiguedad-construccion",
    title: "How to prove the age of a construction",
    category: "Age of buildings",
    seoTitle: "How to prove the age of a construction | MideFinca",
    metaDescription:
      "Historical aerial photography, period documentation and technical certificate: how to prove since when a construction has existed and what it is used for.",
    excerpt: "Age is not declared: it is proven with evidence. What proof works and how it is documented.",
    h1: "How to prove the age of a construction",
    sections: [
      {
        h2: "Age is proven with evidence, not with assertions",
        paragraphs: [
      "Knowing that a construction 'has been there forever' is not enough: for a file you need to demonstrate it. Serious proof combines the current measurement of the building with historical evidence showing it already built on specific dates.",
        ],
      },
      {
        h2: "What evidence is usually used",
        paragraphs: ["The most common evidence, from greater to lesser technical weight:"],
        list: [
      "Historical aerial photography: old flights and orthophotos from different periods show whether the construction already existed on a given date.",
      "Historical mapping and Catastro: old representations of the parcel and its constructions.",
      "Period documentation: deeds, permits, construction invoices, supply contracts or old receipts.",
      "Dated photographs and other documentary testimony.",
        ],
      },
      {
        h2: "The technical certificate of age",
        paragraphs: [
      "The technician measures the construction, analyses the available evidence and issues a certificate documenting what exists today and since when it is known to exist according to the proof. It is commonly used in declarations of new construction, regularisation files and sales.",
      "A serious certificate says how far the proof reaches: if the evidence only allows proving existence since a certain year, that is what will be recorded. And the legal or planning effects of that age depend on the applicable regulations and each file, which is for other professionals to assess.",
        ],
      },
    ],
  },
  {
    slug: "que-es-declaracion-de-obra-nueva",
    title: "What is a declaration of new construction?",
    category: "New construction",
    seoTitle: "What a declaration of new construction is and when it is needed | MideFinca",
    metaDescription:
      "The declaration of new construction is the channel to document constructions that don't appear in the deed. What it is, who is involved and what technical documentation it needs.",
    excerpt: "The usual channel for an existing construction to be recorded in your deed: what it is and who is involved.",
    h1: "What is a declaration of new construction?",
    sections: [
      {
        h2: "Documenting what exists but is not recorded",
        paragraphs: [
      "Many plots have constructions, such as dwellings, barns or annexes, that appear neither in the deed nor in the Land Registry. The declaration of new construction is the notarial instrument by which the owner declares the existence of that construction so it can be incorporated into the property's documentation.",
      "It is important to understand what it does and what it doesn't: it documents the built reality, but it is not by itself a planning legalisation, which depends on the municipal zoning and the regulations applicable to each case.",
        ],
      },
      {
        h2: "Who is involved in the process",
        paragraphs: ["The declaration of new construction is teamwork between several professionals:"],
        list: [
      "The technician: measures the construction, produces the plans and the descriptive certificate, and prepares the georeferenced graphic representation (GML) when the file requires it.",
      "The notary: authorises the deed of declaration of new construction.",
      "The Land Registry: assesses the registration of what was declared.",
      "Catastro: after the deed, the alteration is usually filed so the mapping and data reflect the construction.",
        ],
      },
      {
        h2: "What technical documentation is needed",
        paragraphs: [
      "The basis of the file is technical: measurement of the construction and the parcel, plans of the building, descriptive technical certificate and, in most cases, a GML file with the georeferenced geometry. If the construction is old, a certificate of age is usually a key piece of the file.",
      "The exact requirements may vary by case and by the age of the work; that is why it is advisable to measure and document first, and shape the file afterwards with real data.",
        ],
      },
    ],
  },
  {
    slug: "como-documentar-ampliacion-no-declarada",
    title: "How to document an undeclared extension",
    category: "New construction",
    seoTitle: "How to document an undeclared extension | MideFinca",
    metaDescription:
      "If your home has more square metres than the deed shows, the extension of new construction is the channel to document them. Steps, documentation and precautions.",
    excerpt: "Your home grew but the deed doesn't know: how those square metres are documented step by step.",
    h1: "How to document an undeclared extension",
    sections: [
      {
        h2: "A much more common situation than it seems",
        paragraphs: [
      "Enclosed terraces, added bedrooms, second floors, annexes… It is very frequent for the real home to have more built square metres than the deed and Catastro show. While the extension is not recorded, the documented property and the real one don't match, and that complicates sales, valuations, mortgages and inheritances.",
      "The usual channel to document those metres is the extension of new construction: a notarial deed declaring the additional built surface area.",
        ],
      },
      {
        h2: "The steps of the process",
        paragraphs: ["The reasonable order of work is:"],
        list: [
      "Complete measurement of the home, including the extended part, with updated plan and surface computation.",
      "Descriptive technical certificate of the extension, with the georeferenced graphic representation when the file requires it.",
      "If the extension is old: proof of its age with evidence (historical aerial photography, period documents).",
      "Deed of extension of new construction before a notary and registry filing, which belong to those professionals.",
      "Subsequent update in Catastro with the corresponding documentation.",
        ],
      },
      {
        h2: "Precautions before starting",
        paragraphs: [
      "Viability and requirements depend on the case: the provable age, the planning status of the land and the available documentation shape the file. Documenting an extension and legalising it under planning law are different things, and it is worth knowing which of the two scenarios you are in before spending.",
      "Our part is the technical one: measuring, drawing and certifying the built reality so the notary, the Land Registry and the other professionals work on real data.",
        ],
      },
    ],
  },
  {
    slug: "cuando-es-obligatorio-certificado-eficiencia-energetica",
    title: "When is the energy performance certificate mandatory?",
    category: "Energy efficiency",
    seoTitle: "When the energy performance certificate is mandatory | MideFinca",
    metaDescription:
      "Sale, rental, new construction and renovation: when the regulations require the energy performance certificate, what exceptions exist and how long it is valid.",
    excerpt: "Selling, renting, new construction or renovating: the cases in which the regulations require it and its exceptions.",
    h1: "When is the energy performance certificate mandatory?",
    sections: [
      {
        h2: "The general rule: selling or renting requires it",
        paragraphs: [
      "Since 2013, as a general rule, every property sold or rented in Spain must have an energy performance certificate. It is the document that rates the home or premises from A to G according to its energy consumption and emissions, and that produces the well-known energy label.",
      "In practice you'll notice it at three moments: the sale or rental listing must show the rating, the rental contract requires handing a copy to the tenant and, in a sale, the notary will ask for the registered certificate for the deed.",
        ],
      },
      {
        h2: "Other cases in which it is also required",
        paragraphs: ["Besides sale and rental, the regulations provide for the certificate in other scenarios:"],
        list: [
      "New construction: the newly built building or dwelling obtains its certificate when the work is completed.",
      "Major renovations that substantially modify the envelope or the installations.",
      "Certain public renovation grants and subsidies, which require it as part of the file's documentation.",
      "Public-use buildings over a certain surface area, which must also display the label.",
        ],
      },
      {
        h2: "The exceptions the rule contemplates",
        paragraphs: [
      "The regulations list specific exceptions. The most common for private homes and properties:",
        ],
        list: [
      "Standalone properties with a usable surface area under 50 m².",
      "Properties bought for demolition or major renovation, duly accredited.",
      "Homes for temporary use of less than four months a year, with the corresponding declaration.",
      "Properties protected for their historical value, when the requirements would alter their character, according to the technical report accrediting it.",
        ],
      },
      {
        h2: "How long it lasts and when to renew it",
        paragraphs: [
      "The certificate is valid for a maximum of ten years. After that, it must be renewed. And even while it remains valid, it is worth updating it if relevant renovations have been made (windows, insulation, boiler or aerothermal), because the rating may improve and a better letter helps sell or rent.",
      "The certificate must be registered with the competent body of the autonomous community: in the Canary Islands, the corresponding registry of the regional government. Only with that registration is the label valid for advertising and signing.",
        ],
      },
      {
        h2: "What happens if you sell or rent without a certificate",
        paragraphs: [
      "Not having the certificate does not invalidate the transaction, but it does create practical problems: the listing would be incorrect, the notary can halt the signing until it exists and the responsibility for not providing it falls on the owner. The sensible approach is to commission it in advance: the visit is brief and the certificate is obtained within a few days.",
        ],
      },
      {
        h2: "Who can issue it",
        paragraphs: [
      "The certificate is signed by technicians with the qualifying degree the regulations require. At MideFinca we prepare it with a visit to the property and real data capture, calculate it with the official procedures and register it with the competent body of the Canary Islands, handing you the label ready for the listing and the signing. If you think your case may be exempt, ask us before ruling it out.",
        ],
      },
    ],
  },
  {
    slug: "fisuras-y-grietas-cuando-preocuparse",
    title: "Cracks and fissures: when to worry",
    category: "Structure",
    seoTitle: "Cracks and fissures: when to worry | MideFinca",
    metaDescription:
      "Not every fissure is serious and not every crack is harmless. Learn to tell the usual damage from the warning signs, and when a technical inspection is advisable.",
    excerpt: "Not every fissure is serious and not every crack is harmless: how to tell the usual from the warning signs.",
    h1: "Cracks and fissures: when to worry",
    sections: [
      {
        h2: "First, the terminology: a fissure is not the same as a crack",
        paragraphs: [
      "In technical language, a fissure is a fine opening, usually superficial, that doesn't go through the element; a crack is wider, deeper and usually indicates a more serious movement or failure. The difference matters because most fissures are a finish problem and most cracks deserve, at least, monitoring.",
      "Another useful nuance: what appears in a plasterboard or brick partition, which holds nothing up, is not the same as what appears in a column, a beam, a slab or a load-bearing wall, which are the elements that do hold the building up.",
        ],
      },
      {
        h2: "Common damage that is usually benign",
        paragraphs: ["These appear in almost any building and, in general, don't affect the structure:"],
        list: [
      "Fine fissures at window and door jambs and lintels, where the wall is weakest.",
      "Fissures at joints between different materials: partition and column, ceiling and wall, carpentry and partition.",
      "Superficial cracking of renders and paint due to shrinkage or temperature changes.",
      "Fissures in partition walls that reappear after painting, without growing in width.",
        ],
      },
      {
        h2: "Warning signs that do deserve attention",
        paragraphs: [
      "There are patterns a technician takes seriously because they can indicate ground settlement, overloading, reinforcement corrosion or problems with the structure itself:",
        ],
        list: [
      "Cracks that grow: they lengthen or open over months or with the seasons.",
      "Diagonal cracks at the corners of windows and doors, or slanting across the wall.",
      "Stepped cracks following the joints of brick or block masonry.",
      "Fissures crossing the slab or appearing in columns, beams and load-bearing walls.",
      "Leaning, bulging or spalling concrete with corroded reinforcement in sight, frequent on balconies.",
      "Doors or windows that suddenly rub or don't close, or floors that tilt.",
        ],
      },
      {
        h2: "What you can do before calling a technician",
        paragraphs: [
      "Documenting the damage well greatly helps the diagnosis and costs nothing:",
        ],
        list: [
      "Photograph the damage with a date and a scale reference (a ruler or a coin).",
      "Note when it appeared and whether it coincides with something: nearby works, a renovation, an incident, a rainy season.",
      "Repeat the photo every two or three months from the same point to see if it evolves.",
      "On cracks, a plaster tell-tale or a crack gauge shows whether it is still moving; a technician installs it.",
        ],
      },
      {
        h2: "When a technical inspection is advisable",
        paragraphs: [
      "Ask for a technical assessment if the damage matches any warning sign, if it affects structural elements, if it will condition a sale or a renovation, or if you simply can't rule out that it's serious. Also if an owners' association, an insurer or an administration requires a report from you.",
      "The inspection resolves exactly that doubt: a technician examines the structure, describes and documents the damage, assesses its extent and tells you what to do: monitoring, repair or, where appropriate, testing or a more detailed study. That is the content of the certificate of structural condition, which you can consult on the service page.",
        ],
      },
    ],
  },
{
    slug: "como-segregar-una-finca-en-canarias",
    title: "How to segregate a plot in the Canary Islands",
    category: "Land Registry & deeds",
    seoTitle: "How to segregate a plot in the Canary Islands: steps and documentation | MideFinca",
    metaDescription:
      "What a segregation is, what documentation it needs, why planning regulations must be checked first and what role the town hall, the notary, Catastro and the Land Registry play.",
    excerpt: "What a segregation is, what documentation it needs and why planning regulations must be checked first.",
    h1: "How to segregate a plot in the Canary Islands",
    sections: [
      {
        h2: "What a segregation is",
        paragraphs: [
      "Segregating a plot means dividing it into two or more independent parcels, each with its own geometry, surface area and documentation. It is a common operation in inheritances, partial sales or when a plot is too large for its intended use.",
      "The important thing is to understand that segregating is not just drawing a line on a plan: each resulting parcel must be technically defined and the operation must be possible under the applicable planning regulations.",
        ],
      },
      {
        h2: "Why planning regulations must be checked first",
        paragraphs: [
      "Not every plot can be divided. Each municipality's planning regulations set conditions such as the minimum surface area of the resulting parcels, the required frontages or the necessary access. That is why, before raising a segregation, it must be checked whether the plot allows it and under what conditions.",
      "This prior check avoids spending on measurements and documentation for an operation that later cannot be authorised or registered.",
        ],
      },
      {
        h2: "The technical part: measurement, georeferencing and graphic representation",
        paragraphs: [
      "Once viability is confirmed, the plot is measured and georeferenced, and the new parcels are defined: where the new boundaries run, what surface area each one has and what coordinates its vertices have.",
      "With that definition, the plans of each parcel are produced and, when the procedure requires it, the georeferenced graphic representation in GML format and the positive Informe de Validación Gráfica Alternativa (IVGA, an alternative graphic validation report), where applicable.",
        ],
      },
      {
        h2: "Who is involved in the procedure",
        paragraphs: [
      "Depending on the case, several bodies take part, each in its own role:",
        ],
        list: [
      "The town hall, when planning regulations require authorisation or notification to divide the plot.",
      "The notary, for the deed of division.",
      "The Land Registry, for the registration of the resulting properties.",
      "Catastro, for the update of its graphic representation based on what is documented.",
        ],
      },
      {
        h2: "What documentation is needed",
        paragraphs: [
      "To assess a segregation it is advisable to gather the plot's title deed, the cadastral reference and, if available, a Land Registry extract. With the measurement and the documentary review, the technical report and the plans of the resulting parcels are prepared, which are the basis on which the notary and the Land Registry then work.",
        ],
      },
    ],
  },
  {
    slug: "como-agrupar-fincas-en-canarias",
    title: "How to group plots in the Canary Islands",
    category: "Land Registry & deeds",
    seoTitle: "How to group several plots into one in the Canary Islands | MideFinca",
    metaDescription:
      "What a plot grouping is, what must be checked beforehand and what technical documentation is prepared for the notary, the Land Registry and Catastro.",
    excerpt: "What a plot grouping is, what must be checked beforehand and what documentation is prepared.",
    h1: "How to group plots in the Canary Islands",
    sections: [
      {
        h2: "What a plot grouping is",
        paragraphs: [
      "Grouping plots means joining two or more parcels into a single resulting plot, with a new documented geometry and surface area. It is frequent after buying the neighbouring plot, in inheritances with several parcels or when it is convenient to manage the whole as a single property.",
        ],
      },
      {
        h2: "What must be checked beforehand",
        paragraphs: [
      "Not all plots can be grouped automatically. Before raising the operation it must be checked that the parcels are adjoining, the ownership and registered charges of each one must be reviewed and the existing documentation must be confirmed to be consistent with the physical reality.",
      "If any of the plots has discrepancies between the deed, Catastro and reality, it is advisable to resolve them or at least know them before grouping.",
        ],
      },
      {
        h2: "The technical part of the grouping",
        paragraphs: [
      "The technical work consists of measuring and georeferencing the whole, defining the geometry of the resulting plot and producing the plans of the previous situation and of the grouped plot. When the procedure requires it, the graphic representation in GML format and the positive Informe de Validación Gráfica Alternativa (IVGA) are also prepared, where applicable.",
        ],
      },
      {
        h2: "Formalisation: notary, Land Registry and Catastro",
        paragraphs: [
      "The grouping is normally formalised by deed before a notary and registered at the Land Registry. Afterwards the cadastral representation is updated so that Catastro's drawing matches the new documented reality.",
      "Our part is the technical one: the measurement, the plans and the graphic representation those procedures need. The deed and the registration belong to the notary and the Land Registry.",
        ],
      },
    ],
  },
  {
    slug: "primera-ocupacion-edificaciones-fuera-de-ordenacion",
    title: "First occupation of non-conforming buildings",
    category: "Planning",
    seoTitle: "First occupation of non-conforming buildings | MideFinca",
    metaDescription:
      "What documentation may be needed to process the first occupation of a building in fuera de ordenación status and why the decision belongs to the town hall.",
    excerpt: "What documentation may be needed and why the decision always belongs to the town hall.",
    h1: "First occupation of non-conforming buildings: what documentation may be needed",
    sections: [
      {
        h2: "What 'fuera de ordenación' means",
        paragraphs: [
      "A building is fuera de ordenación (legally non-conforming) when it does not conform to the current planning regulations, usually because it was built under earlier rules or without them. That status does not necessarily prevent using the property, but it conditions which procedures and which interventions are possible on it.",
        ],
      },
      {
        h2: "What first occupation means in this context",
        paragraphs: [
      "First occupation or use is the procedure by which it is accredited that a building meets the required conditions to be used. When the building is fuera de ordenación, the procedure can only be raised if the property's planning regime allows it, something that must be studied individually before preparing anything.",
        ],
      },
      {
        h2: "What technical documentation may be needed",
        paragraphs: [
      "Depending on the municipality and the case, the file may require:",
        ],
        list: [
      "A technical report on the current state of the building and its background.",
      "Measurement and plans of the current state.",
      "A certificate of age, to prove since when the construction has existed.",
      "Habitability documentation, for dwellings and where applicable.",
        ],
      },
      {
        h2: "Who decides the procedure",
        paragraphs: [
      "First occupation is resolved by the competent town hall according to planning regulations. Well-prepared technical documentation is the condition for the file to be processed, but no technician can guarantee the granting: the decision is municipal.",
        ],
      },
    ],
  },
  {
    slug: "que-es-uso-consolidado-canarias",
    title: "What a consolidated use is and how to prove it in the Canary Islands",
    category: "Planning",
    seoTitle: "What is a consolidated use and how to prove it in the Canary Islands | MideFinca",
    metaDescription:
      "What it means for a use to be consolidated (uso consolidado), what evidence proves it and who can administratively recognise it in the Canary Islands.",
    excerpt: "What it means for a use to be consolidated, what evidence proves it and who can recognise it.",
    h1: "What a consolidated use is and how to prove it in the Canary Islands",
    sections: [
      {
        h2: "What a consolidated use is",
        paragraphs: [
      "In general terms, a uso consolidado (consolidated use) is a use that has existed continuously since a previous planning situation and that the regulations may come to recognise. It is not an automatic label: its existence must be studied individually, case by case.",
        ],
      },
      {
        h2: "What evidence serves to prove it",
        paragraphs: [
      "Accrediting a consolidated use consists of demonstrating with evidence what use the building has, since when and in what conditions. The usual evidence includes:",
        ],
        list: [
      "Historical aerial photography and mapping from different periods.",
      "Period documentation: contracts, receipts, old licences, utilities.",
      "The technical check of the property's current state and use.",
        ],
      },
      {
        h2: "Who recognises the consolidated use",
        paragraphs: [
      "The administrative recognition of the consolidated use, when necessary, belongs to the competent Administration in accordance with the applicable planning regulations. The technical work provides the objective basis: a report documenting the characteristics, age and conditions of the existing use with the evidence analysed.",
        ],
      },
    ],
  },
  {
    slug: "que-es-cedula-urbanistica",
    title: "What a cédula urbanística is and what it is for",
    category: "Planning",
    seoTitle: "What is a cédula urbanística and what is it for | MideFinca",
    metaDescription:
      "The cédula urbanística is the official information on what can be done on a parcel: land classification, uses and parameters. Who issues it and when to request it.",
    excerpt: "The official document stating what can be done on a parcel: who issues it and when it is worth requesting.",
    h1: "What a cédula urbanística is and what it is for",
    sections: [
      {
        h2: "The official planning information of a parcel",
        paragraphs: [
      "The cédula urbanística, or planning information certificate, is the official document informing about the planning conditions of a parcel or property: land classification and categorisation, permitted uses, building parameters and any other applicable provisions of the municipal planning.",
        ],
      },
      {
        h2: "What it is for in practice",
        paragraphs: ["It is usually needed in situations like these:"],
        list: [
      "Before buying a parcel, to know what can be built or which uses it allows.",
      "Before designing, to know the starting parameters.",
      "When a notary, a bank or an administration asks for official planning information about the property.",
      "To confirm a parcel's conditions before starting any building procedure.",
        ],
      },
      {
        h2: "Who issues it and who requests it",
        paragraphs: [
      "The cédula urbanística is issued by the competent town hall. The owner can request it directly or commission the processing to a technician, who correctly identifies and documents the parcel, prepares the application and then explains the content of the municipal reply in clear terms.",
      "It is worth keeping in mind that the cédula informs about the planning conditions, but the specific viability of a project is confirmed with its technical project and, where applicable, the corresponding licence.",
        ],
      },
    ],
  },
  {
    slug: "que-son-alineaciones-y-rasantes",
    title: "What alineaciones y rasantes are and why they are needed before building",
    category: "Planning",
    seoTitle: "What are alineaciones y rasantes | MideFinca",
    metaDescription:
      "The alineación marks the building's position relative to the street and the rasante sets its reference level. What they are, who issues them and why they are needed before building.",
    excerpt: "The alineación marks the position relative to the street and the rasante the reference level: that is why they are needed before building.",
    h1: "What alineaciones y rasantes are and why they are needed before building",
    sections: [
      {
        h2: "Alineación: the building's position relative to the street",
        paragraphs: [
      "The alineación (building line) is the line marking how far the building may reach in relation to the public road, according to the planning rules. It is not the same as the parcel's boundary: the alineación is a planning determination and the boundary is the limit of the property. They may coincide or not.",
        ],
      },
      {
        h2: "Rasante: the reference level for building",
        paragraphs: [
      "The rasante is the official level serving as reference for the future building: at what height the building sits relative to the road. Levels (cotas) are, simply put, the heights of each point measured against a reference. Knowing the rasante precisely avoids height errors that later are expensive or impossible to correct.",
        ],
      },
      {
        h2: "Why they are needed before designing and building",
        paragraphs: [
      "Designing without knowing the official alineación and rasante risks a project that does not fit the planning rules or having to redo it during the licence process. That is why they are requested at the start, as input data for the project.",
        ],
      },
      {
        h2: "Who issues them and what technical documentation is needed",
        paragraphs: [
      "When the procedure requires it, the official alineaciones y rasantes are issued by the town hall. To request them or to define them technically, a precise topographic survey of the parcel and its surroundings relative to the road is needed, with its plans and levels. That technical and topographic documentation is what we prepare at MideFinca, together with the submission of the application where applicable.",
        ],
      },
    ],
  },
  {
    slug: "comunicacion-previa-obra-cuando-se-utiliza",
    title: "Prior notification of works: when it is used and what documentation you need",
    category: "Planning",
    seoTitle: "Prior notification of works: when it is used | MideFinca",
    metaDescription:
      "Which works can be processed by comunicación previa (prior notification), what technical documentation they usually need and why it depends on each municipality's ordinance.",
    excerpt: "Which works can be processed by prior notification and why it depends on each municipality's ordinance.",
    h1: "Prior notification of works: when it is used and what documentation you need",
    sections: [
      {
        h2: "What prior notification is",
        paragraphs: [
      "The comunicación previa (prior notification) is a simplified procedure by which certain works are notified to the town hall, providing the documentation required by the municipal ordinance. It is more agile than a licence, but it is not available for every work.",
        ],
      },
      {
        h2: "Not every small work is prior notification",
        paragraphs: [
      "It is a frequent mistake to assume that 'minor works' are always processed by prior notification. The classification of each work depends on each municipality's ordinance and on the type of work: whether it affects the structure, the façade or the layout, among other factors. The correct approach is to check which procedure applies before filing anything.",
        ],
      },
      {
        h2: "What technical documentation is usually needed",
        paragraphs: ["Depending on the case and the ordinance, the notification may require:"],
        list: [
      "A descriptive report of the works.",
      "Plans or measurement of the current and the intended state.",
      "The works execution budget.",
      "Property documentation: cadastral reference and basic registry data.",
        ],
      },
      {
        h2: "And if the works need a licence",
        paragraphs: [
      "If the check determines that the works require a works licence, the corresponding procedure must be prepared, including the technical project when the works require it. Knowing this from the start avoids beginning with the wrong procedure.",
        ],
      },
    ],
  },
  {
    slug: "licencia-obra-mayor-documentacion",
    title: "Major works licence: what documentation you need",
    category: "Planning",
    seoTitle: "Major works licence: project and documentation | MideFinca",
    metaDescription:
      "Which works require a licencia de obra mayor (major works licence), what technical documentation the file needs and who grants the licence in the Canary Islands.",
    excerpt: "Which works require a major works licence and what technical documentation the file needs.",
    h1: "Major works licence: project, documentation and processing",
    sections: [
      {
        h2: "Which works require a major works licence",
        paragraphs: [
      "Depending on the case and the municipal ordinance, a licencia de obra mayor (major works licence) is usually required for new construction, extensions, certain renovations, refurbishments, structural interventions and other works subject to licence. Checking which procedure applies to each work is the first step of the job.",
        ],
      },
      {
        h2: "What technical documentation the file needs",
        paragraphs: ["A major works licence application usually includes:"],
        list: [
      "Technical project: report, plans and the documentation required according to the works.",
      "Survey and measurement of the current state, where applicable.",
      "The works execution budget.",
      "Property documentation: cadastral reference, title deed and background.",
        ],
      },
      {
        h2: "Who grants the licence",
        paragraphs: [
      "The licence is granted or denied by the town hall according to planning regulations, and no technician can guarantee its granting. What does depend on the technical side is that the file goes in complete and well prepared, without errors that cause correction requests and delays.",
        ],
      },
      {
        h2: "How long it may take",
        paragraphs: [
      "Timeframes depend on the municipality and the specific file, and it is not prudent to promise dates. The advisable approach is to prepare the documentation with enough lead time and to respond quickly to any technical requirements raised during processing.",
        ],
      },
    ],
  },
  {
    slug: "que-es-division-horizontal",
    title: "What is the horizontal division (división horizontal) and when is it needed",
    category: "Land Registry & deeds",
    seoTitle: "What is the división horizontal and when is it needed | MideFinca",
    metaDescription:
      "The división horizontal allows a building to be legally divided into independent units. What it documents, what participation quotas are and when measurement is needed.",
    excerpt: "The legal figure that turns a building into independent units: what it documents and when measurement is needed.",
    h1: "What is the horizontal division (división horizontal) and when is it needed",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "One building, several independent properties",
        paragraphs: [
          "The división horizontal (horizontal division) is the Spanish legal figure that allows a building to be divided into independent units, each with its own ownership: flats, commercial premises, storage rooms or parking spaces. It underpins the horizontal property regime, the usual system for residential buildings with common elements.",
          "Each unit is described in the constitutive title: its floor area, its layout, the common elements allocated to it and its participation quota in the building as a whole.",
        ],
      },
      {
        h2: "When technical measurement work is needed",
        paragraphs: ["The technical side becomes important in situations such as these:"],
        list: [
          "Establishing the horizontal division of a new or existing building that was never divided.",
          "Modifying an existing division: joining or separating units, changing floor areas or uses.",
          "Correcting differences between what was built and what the title describes.",
          "Providing measured data for the sale, inheritance or valuation of a unit.",
        ],
      },
      {
        h2: "Why quotas and floor areas must be measured",
        paragraphs: [
          "Participation quotas are usually calculated in relation to the floor area or value of each unit, and community expenses and meeting majorities depend on them. If the starting area is not the real one, everything built on it is skewed.",
          "Prior technical measurement, unit by unit, avoids deeds based on figures inherited from old plans or approximate declarations. The deed and the registration correspond to the notary and the Land Registry; the measured data base corresponds to the technician.",
        ],
      },
    ],
    relatedServices: ["division-horizontal-canarias", "certificado-superficies", "declaracion-obra-nueva"],
    relatedProblems: ["dividir-edificio-en-unidades"],
  },
  {
    slug: "cambio-de-uso-local-a-vivienda-canarias",
    title: "Change of use from commercial premises to dwelling in the Canary Islands: what to check first",
    category: "Planning",
    seoTitle: "Change of use from premises to dwelling: what to check | MideFinca",
    metaDescription:
      "Before converting commercial premises into a dwelling in the Canary Islands you must check planning rules, the municipal ordinance, the conditions of the premises and the community statutes. We explain the feasibility study.",
    excerpt: "What must be checked, before building or buying, to know whether premises can become a dwelling.",
    h1: "Change of use from commercial premises to dwelling in the Canary Islands: what to check first",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "Feasibility first, works later",
        paragraphs: [
          "A change of use from commercial premises to a dwelling requires two prior checks: that the planning rules allow it and that the space can meet the conditions required of a dwelling. Buying or building without checking both is the most frequent cause of blocked projects.",
          "The feasibility study answers that double question with data: the applicable rules and the measured physical conditions.",
        ],
      },
      {
        h2: "Which conditions are checked in the premises",
        paragraphs: ["The exact conditions depend on the rules applicable in each municipality, but the study usually reviews:"],
        list: [
          "Openings to outdoor space or a courtyard allowing natural light and ventilation for the habitable rooms.",
          "Clear height and layout, to check whether a compliant dwelling layout fits.",
          "Available or possible access points, bathrooms and service cores.",
          "The statutes of the owners' community, which may condition or limit the change of use.",
        ],
      },
      {
        h2: "Which procedure may apply",
        paragraphs: [
          "Depending on the municipality and the scope of the works, the change of use may require anything from a prior notification or responsible declaration to a planning permit with a technical project. The assessment and the resolution always correspond to the town hall.",
          "If the study is positive, the next step is to prepare the technical documentation for the applicable procedure. If it is negative, we tell you before you invest in works.",
        ],
      },
    ],
    relatedServices: ["cambio-de-uso-local-a-vivienda", "certificado-habitabilidad", "comunicacion-previa-obra"],
    relatedProblems: ["convertir-local-en-vivienda", "que-permiso-necesito-reforma"],
  },
  {
    slug: "que-es-ivga-informe-validacion-grafica",
    title: "What is the IVGA: the Alternative Graphic Validation Report",
    category: "Catastro",
    seoTitle: "What is the IVGA (Alternative Graphic Validation Report) | MideFinca",
    metaDescription:
      "The IVGA is the report by which a competent technician validates the georeferenced graphic representation of a plot. When it is used in Catastro procedures and what a positive IVGA means.",
    excerpt: "The report by which a technician graphically validates the representation of a plot: when it is used and what 'positive' means.",
    h1: "What is the IVGA: the Alternative Graphic Validation Report",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "A technical validation of the plot's geometry",
        paragraphs: [
          "The IVGA (Informe de Validación Gráfica Alternativa, Alternative Graphic Validation Report) is a technical document by which a competent technician validates the georeferenced graphic representation of a plot: it confirms that the measured geometry (vertices, surface and coordinates) is coherent with the reality on the ground.",
          "It is used within Catastro procedures as a technical alternative to individually notifying the adjoining owners: instead of requiring their agreement, the graphic representation is backed by a technician's validation.",
        ],
      },
      {
        h2: "When it may be necessary",
        paragraphs: ["Not every graphic procedure requires an IVGA. Depending on the case, it may be necessary or advisable in situations such as these:"],
        list: [
          "Corrections of surface or geometry in Catastro when the recorded data differ from the measured reality.",
          "Submission of graphic representations (for example, GML files) in coordination procedures between Catastro and the Land Registry.",
          "Declarations or rectifications in which the Catastro authority requires technical backing for the geometry provided.",
        ],
      },
      {
        h2: "What a positive IVGA means, and what it does not",
        paragraphs: [
          "A positive IVGA means that the technician has validated the graphic representation submitted. It is an important technical backing, but it is not a title of ownership, it does not by itself legalise a construction or a division, and it does not replace the resolution of the procedure.",
          "The processing and the resolution correspond to Catastro (or the relevant body). Our work is to measure correctly, prepare the graphic documentation and, where applicable, issue the technical validation so that the file moves forward on solid data.",
        ],
      },
      {
        h2: "Who can issue it",
        paragraphs: [
          "The IVGA is issued by a competent technician (architects, technical architects, engineers and other graduates enabled by the cadastral regulations), who assumes technical responsibility for the validation. That is why the basis of the whole report is a well-done georeferenced measurement on the ground.",
        ],
      },
    ],
    relatedServices: ["gml-catastro-registro", "correccion-discrepancias-catastrales", "georreferenciacion-fincas"],
    relatedProblems: ["catastro-escritura-no-coinciden", "necesito-gml"],
  },
  {
    slug: "como-rectificar-superficie-catastro",
    title: "How to rectify the surface area of your plot in Catastro",
    category: "Catastro",
    seoTitle: "How to rectify your plot's surface area in Catastro | MideFinca",
    metaDescription:
      "If Catastro records fewer (or more) square metres than the real ones, the surface area can be rectified by providing a georeferenced measurement. What to check, what to document and how the procedure works.",
    excerpt: "What to check and what to document when the square metres recorded in Catastro do not match your real plot.",
    h1: "How to rectify the surface area of your plot in Catastro",
    updatedAt: "2026-08-24",
    sections: [
      {
        h2: "When Catastro's square metres are not the real ones",
        paragraphs: [
          "Catastro is a database with mainly tax purposes, and its recorded surface area does not always match reality or the deed. The difference can come from old measurements, displaced mapping, historical subdivisions or transcription errors.",
          "Before starting any procedure you need to know how large the plot really is: without a georeferenced measurement, any claim rests only on opinions.",
        ],
      },
      {
        h2: "What to check before requesting anything",
        paragraphs: ["Before raising a rectification, it is advisable to gather and check:"],
        list: [
          "The real georeferenced measurement of the plot, with its vertices and its surface area.",
          "The surface area and boundaries described in the deed or title of ownership.",
          "The current cadastral mapping of your parcel and of the adjoining ones, to detect overlaps or gaps.",
          "The possible effects of the new surface area (tax or registry-related), which should be assessed with your adviser before acting.",
        ],
      },
      {
        h2: "How the rectification is raised",
        paragraphs: [
          "Once the measurement is done, the graphic documentation of the plot is prepared (georeferenced representation and, where applicable, the GML file) together with the surface correction proposal. Depending on the case, Catastro may require notification to the adjoining owners or technical backing such as the IVGA.",
          "It is important to be clear that Catastro is the body that processes and resolves the procedure. Our work is to measure, document and technically substantiate the proposal; acceptance depends on the file and cannot be promised.",
        ],
      },
      {
        h2: "What changes and what does not",
        paragraphs: [
          "Rectifying the surface area in Catastro does not by itself modify the Land Registry or the deed. If the registered area must also be updated, an additional procedure may be necessary (for example, an excess of area procedure, exceso de cabida), which is assessed case by case.",
          "That is why the work should be approached with a complete view: measurement, Catastro and, if necessary, coordination with the Land Registry through a notary.",
        ],
      },
    ],
    relatedServices: ["correccion-discrepancias-catastrales", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedProblems: ["catastro-escritura-no-coinciden", "mi-finca-tiene-mas-o-menos-metros"],
  },
];
